import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useAsync } from 'react-use';

import { ScorecardsClient } from '../api/ScorecardsClient';
import type { AggregateBy, CategoryData, TeamDescendantsTree } from '../types';
import { toStandardISO } from '../utils';

const normalizeTeamTree = (
  teamTree?: TeamDescendantsTree | TeamDescendantsTree[],
): TeamDescendantsTree[] => {
  if (!teamTree) return [];
  if (!Array.isArray(teamTree)) return [teamTree];
  if (!teamTree.length || !teamTree[0]) return [];
  return teamTree;
};

export const useGetCategoriesData = (
  teamTree?: TeamDescendantsTree | TeamDescendantsTree[],
  startDate?: string,
  endDate?: string,
  aggregateBy: AggregateBy = 'month',
  refreshKey?: number,
): { value: CategoryData[] | null; loading: boolean } => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const client = useMemo(
    () => new ScorecardsClient({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );

  const teamTreeIds = useMemo(
    () => JSON.stringify(normalizeTeamTree(teamTree)),
    [teamTree],
  );

  const { value: categoriesData, loading } = useAsync(async () => {
    const teamIds = JSON.parse(teamTreeIds);
    if (!teamIds.length) return null;

    try {
      return await client.getCategoriesData({
        teamIds,
        startDate:
          startDate ??
          toStandardISO(DateTime.now().minus({ months: 5 }).startOf('month')),
        endDate: endDate ?? toStandardISO(DateTime.now().endOf('month')),
        aggregateBy,
      });
    } catch {
      return null;
    }
  }, [client, teamTreeIds, startDate, endDate, aggregateBy, refreshKey]);

  return {
    value:
      categoriesData?.filter((item: CategoryData) => item !== null) ?? null,
    loading,
  };
};